import { ReactNode, useMemo, useState } from 'react';
import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { ApartmentOutlined, AssignmentIndOutlined, BadgeOutlined, PeopleAltOutlined, WorkOutlineOutlined, SyncOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';

import { GetDashboardResponse, NetworkAgency } from 'core/dashboard/api';
import DashboardPage, { HAIRLINE, Panel, PanelHeader } from './dashboard.page';

const formatNumber = (value: number) => value.toLocaleString('fr-FR');

/* ------------------------------------------------------------------ */
/* Network totals                                                      */
/* ------------------------------------------------------------------ */

const TotalTile = ({ icon, label, value, hint, color, bg }: { icon: ReactNode; label: string; value: number; hint?: string; color: string; bg: string }) => (
  <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
    <Box sx={{ width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: bg, color, flexShrink: 0, '& svg': { fontSize: '1.25rem' } }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em', color: 'text.primary' }}>{formatNumber(value)}</Typography>
      <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
        {label}{hint && <Box component="span" sx={{ color: '#B0B7C3' }}> · {hint}</Box>}
      </Typography>
    </Box>
  </Stack>
);

const NetworkTotals = ({ data }: { data: GetDashboardResponse }) => {
  const totals = data.network?.totals;
  if (!totals) return null;

  const syncedRatio = totals.temp_workers_count > 0 ? Math.round((totals.synced_temp_workers_count / totals.temp_workers_count) * 100) : 0;

  return (
    <Panel sx={{ px: 3, py: 2.5 }}>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase', color: 'text.secondary', mb: 2 }}>
        Le réseau Chronos
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 3 }}>
        <TotalTile icon={<ApartmentOutlined />} label="Agences ouvertes" value={totals.agencies_count} color="#2E6BEB" bg="#ECF2FF" />
        <TotalTile icon={<BadgeOutlined />} label="Agents" value={totals.agents_count} color="#7C5CFF" bg="#F1EDFF" />
        <TotalTile icon={<PeopleAltOutlined />} label="Intérimaires suivis" value={totals.temp_workers_count} color="#1CC266" bg="#E5FFF2" />
        <TotalTile icon={<SyncOutlined />} label="Synchronisés Anaël" value={totals.synced_temp_workers_count} hint={`${syncedRatio} %`} color="#12B5C9" bg="#E6F9FB" />
        <TotalTile icon={<WorkOutlineOutlined />} label="Missions publiées" value={totals.offers_count} color="#FF8D3A" bg="#FFF8EF" />
        <TotalTile icon={<AssignmentIndOutlined />} label="Candidatures reçues" value={totals.applications_count} color="#F31249" bg="#FFF0EB" />
      </Box>
    </Panel>
  );
};

/* ------------------------------------------------------------------ */
/* Per-agency breakdown                                                */
/* ------------------------------------------------------------------ */

type SortKey = keyof Omit<NetworkAgency, 'id'>;

const COLUMNS: { key: SortKey; label: string; hint?: string; align?: 'left' | 'right' }[] = [
  { key: 'name', label: 'Agence', align: 'left' },
  { key: 'temp_workers', label: 'Intérimaires', align: 'right' },
  { key: 'synced_temp_workers', label: 'Synchronisés', align: 'right' },
  { key: 'new_temp_workers', label: 'Inscrits', hint: '30 j', align: 'right' },
  { key: 'offers', label: 'Missions', hint: '30 j', align: 'right' },
  { key: 'applications', label: 'Candidatures', hint: '30 j', align: 'right' },
  { key: 'agents', label: 'Agents', align: 'right' },
];

const AgenciesBreakdown = ({ data }: { data: GetDashboardResponse }) => {
  const [sortKey, setSortKey] = useState<SortKey>('new_temp_workers');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const agencies = useMemo(() => data.network?.agencies ?? [], [data.network]);
  const windowDays = data.network?.totals.window_days ?? 30;

  const sorted = useMemo(() => {
    const rows = [...agencies];
    rows.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      const cmp = typeof av === 'string' && typeof bv === 'string' ? av.localeCompare(bv, 'fr') : Number(av) - Number(bv);
      return direction === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [agencies, sortKey, direction]);

  const maxNew = Math.max(1, ...agencies.map((a) => a.new_temp_workers));

  const onSort = (key: SortKey) => {
    if (key === sortKey) setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setDirection(key === 'name' ? 'asc' : 'desc'); }
  };

  if (!data.network) return null;

  return (
    <Panel sx={{ overflow: 'hidden' }}>
      <PanelHeader title="Détail par agence" subtitle={`${agencies.length} agences ouvertes · activité des ${windowDays} derniers jours`} />
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760, '& th, & td': { borderBottom: `1px solid ${HAIRLINE}`, px: 3, py: 1.25 }, '& th': { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase', color: 'text.secondary', whiteSpace: 'nowrap', bgcolor: '#FAFBFC' }, '& td': { fontSize: '0.875rem' }, '& tbody tr:last-child td': { borderBottom: 0 }, '& tbody tr:hover': { bgcolor: '#F7F8FA' } }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column.key} align={column.align} sortDirection={sortKey === column.key ? direction : false}>
                  <TableSortLabel active={sortKey === column.key} direction={sortKey === column.key ? direction : 'desc'} onClick={() => onSort(column.key)}>
                    {/* Single node so right-aligned (row-reverse) labels keep "Label · hint" order */}
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                      {column.label}{column.hint && <Box component="span" sx={{ ml: 0.5, color: '#B0B7C3', fontWeight: 500, textTransform: 'none' }}>{column.hint}</Box>}
                    </Box>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((agency) => (
              <TableRow key={agency.id}>
                <TableCell>
                  <Typography component={Link} to={`/dashboard/agencies/${agency.id}`} sx={{ fontSize: 'inherit', fontWeight: 600, color: 'text.primary', textDecoration: 'none', '&:hover': { color: 'secondary.main' } }}>
                    {agency.name}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(agency.temp_workers)}</TableCell>
                <TableCell align="right">
                  {formatNumber(agency.synced_temp_workers)}
                  <Box component="span" sx={{ ml: 0.75, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {agency.temp_workers > 0 ? `${Math.round((agency.synced_temp_workers / agency.temp_workers) * 100)} %` : '—'}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1}>
                    <Box sx={{ width: 64, height: 6, borderRadius: 3, bgcolor: '#EEF1F5', overflow: 'hidden' }}>
                      <Box sx={{ width: `${(agency.new_temp_workers / maxNew) * 100}%`, height: '100%', bgcolor: 'secondary.main', borderRadius: 3 }} />
                    </Box>
                    <Box component="span" sx={{ minWidth: 24, textAlign: 'right' }}>{formatNumber(agency.new_temp_workers)}</Box>
                  </Stack>
                </TableCell>
                <TableCell align="right">{formatNumber(agency.offers)}</TableCell>
                <TableCell align="right">{formatNumber(agency.applications)}</TableCell>
                <TableCell align="right">{formatNumber(agency.agents)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Panel>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const ChronosGlobalPage = () => (
  <DashboardPage
    queryKey="global_dashboard"
    url="/admin/api/dashboard/global"
    title="Chronos Global"
    subtitle="Vue consolidée de toutes les agences du réseau"
    renderTop={(data) => <NetworkTotals data={data} />}
    renderMiddle={(data) => <AgenciesBreakdown data={data} />}
  />
);

export default ChronosGlobalPage;
